/**
 * The language Iris greets a maker in, before they have said a word. An account's saved
 * language wins (set once sign-in exists); otherwise the browser's; otherwise English.
 * Once the maker talks, Iris simply follows the language they use.
 */
export type IrisLanguage = "en" | "nl" | "de" | "fr" | "es" | "tr";
const SUPPORTED: IrisLanguage[] = ["en", "nl", "de", "fr", "es", "tr"];

let accountLanguage = "";

/** Called by sign-in (Google's `locale` claim, or the language saved in the profile). */
export function setAccountLanguage(language: string): void {
  accountLanguage = language;
}

function base(tag: string): IrisLanguage | "" {
  const code = tag.trim().toLowerCase().split(/[-_]/)[0] as IrisLanguage;
  return SUPPORTED.includes(code) ? code : "";
}

export function makerLanguage(): IrisLanguage {
  const browser = typeof navigator === "undefined" ? [] : [...(navigator.languages || []), navigator.language || ""];
  for (const tag of [accountLanguage, ...browser]) {
    const found = tag && base(tag);
    if (found) return found;
  }
  return "en";
}

/** The full tag for Iris's model and voice, e.g. "nl-NL"; the base language when unknown. */
export function makerLanguageTag(): string {
  const language = makerLanguage();
  const browser = typeof navigator === "undefined" ? [] : [...(navigator.languages || [])];
  const exact = [accountLanguage, ...browser].find((tag) => tag && base(tag) === language && tag.includes("-"));
  return exact || language;
}

const INTAKE_WELCOME: Record<IrisLanguage, string> = {
  en: "Hi, I’m Iris. Let’s start with your song: add the file below, then paste the lyrics. I’ll read them and suggest three directions for your film.",
  nl: "Hoi, ik ben Iris. Laten we beginnen met je nummer: voeg hieronder het bestand toe en plak daarna de songtekst. Ik lees ze en stel drie richtingen voor je film voor.",
  de: "Hallo, ich bin Iris. Fangen wir mit deinem Song an: Füge unten die Datei hinzu und füge dann den Songtext ein. Ich lese ihn und schlage dir drei Richtungen für deinen Film vor.",
  fr: "Bonjour, je suis Iris. Commençons par ta chanson : ajoute le fichier ci-dessous, puis colle les paroles. Je les lis et je te propose trois directions pour ton film.",
  es: "Hola, soy Iris. Empecemos por tu canción: añade el archivo abajo y luego pega la letra. La leo y te propongo tres direcciones para tu película.",
  tr: "Merhaba, ben Iris. Şarkınla başlayalım: dosyayı aşağıya ekle, sonra sözleri yapıştır. Onları okuyup filmin için üç yön önereceğim.",
};

export function intakeWelcome(): string {
  return INTAKE_WELCOME[makerLanguage()];
}

const GUIDE_GREETING: Record<IrisLanguage, { studio: string; production: string }> = {
  en: {
    studio: "Hi, I'm Iris. Ask me anything about Caprica, or where to start.",
    production: "Hi, I'm Iris. Ask me what to do next, or what anything on this page means.",
  },
  nl: {
    studio: "Hoi, ik ben Iris. Vraag me alles over Caprica, of waar je kunt beginnen.",
    production: "Hoi, ik ben Iris. Vraag me wat je nu kunt doen, of wat iets op deze pagina betekent.",
  },
  de: {
    studio: "Hallo, ich bin Iris. Frag mich alles über Caprica oder wo du anfangen kannst.",
    production: "Hallo, ich bin Iris. Frag mich, was als Nächstes kommt oder was etwas auf dieser Seite bedeutet.",
  },
  fr: {
    studio: "Bonjour, je suis Iris. Demande-moi tout sur Caprica, ou par où commencer.",
    production: "Bonjour, je suis Iris. Demande-moi quoi faire ensuite, ou ce que signifie un élément de cette page.",
  },
  es: {
    studio: "Hola, soy Iris. Pregúntame lo que quieras sobre Caprica o por dónde empezar.",
    production: "Hola, soy Iris. Pregúntame qué hacer ahora o qué significa algo de esta página.",
  },
  tr: {
    studio: "Merhaba, ben Iris. Caprica hakkında her şeyi ya da nereden başlayacağını sorabilirsin.",
    production: "Merhaba, ben Iris. Sırada ne olduğunu ya da bu sayfadaki bir şeyin ne anlama geldiğini sor.",
  },
};

export function guideGreeting(inProduction: boolean): string {
  const greeting = GUIDE_GREETING[makerLanguage()];
  return inProduction ? greeting.production : greeting.studio;
}

/** Fixed chips and consent lines around Iris's icebreaker, in the maker's language. */
export interface IcebreakerCopy {
  continue: string;
  skip: string;
  directions: string;
  forget: string;
  rememberAsk: string;
  rememberYes: string;
  rememberNo: string;
  remembered: string;
  notRemembered: string;
  remembersLabel: string;
  forgotten: string;
}

const ICEBREAKER: Record<IrisLanguage, IcebreakerCopy> = {
  en: {
    continue: "Continue", skip: "Skip", directions: "Show me the directions", forget: "Forget my taste",
    rememberAsk: "Shall I remember what you like, so I understand your next film straight away?",
    rememberYes: "Yes, please", rememberNo: "No thanks",
    remembered: "Remembered. Next time I'll just ask whether it still fits.",
    notRemembered: "Fine, I won't remember anything.", remembersLabel: "Iris remembers your taste",
    forgotten: "Forgotten. I'll ask you again next time.",
  },
  nl: {
    continue: "Verder", skip: "Sla over", directions: "Toon de richtingen", forget: "Vergeet mijn smaak",
    rememberAsk: "Zal ik onthouden wat je mooi vindt, zodat ik je volgende film meteen beter snap?",
    rememberYes: "Ja, graag", rememberNo: "Nee, liever niet",
    remembered: "Onthouden. Volgende keer vraag ik alleen of het nog klopt.",
    notRemembered: "Prima, ik onthoud niets.", remembersLabel: "Iris onthoudt je smaak",
    forgotten: "Vergeten. Ik vraag het je volgende keer gewoon opnieuw.",
  },
  de: {
    continue: "Weiter", skip: "Überspringen", directions: "Zeig mir die Richtungen", forget: "Vergiss meinen Geschmack",
    rememberAsk: "Soll ich mir merken, was dir gefällt, damit ich deinen nächsten Film sofort besser verstehe?",
    rememberYes: "Ja, gern", rememberNo: "Nein, lieber nicht",
    remembered: "Gemerkt. Nächstes Mal frage ich nur, ob es noch passt.",
    notRemembered: "In Ordnung, ich merke mir nichts.", remembersLabel: "Iris merkt sich deinen Geschmack",
    forgotten: "Vergessen. Nächstes Mal frage ich einfach wieder.",
  },
  fr: {
    continue: "Continuer", skip: "Passer", directions: "Montre-moi les directions", forget: "Oublie mes goûts",
    rememberAsk: "Je retiens ce que tu aimes, pour mieux comprendre ton prochain film ?",
    rememberYes: "Oui, volontiers", rememberNo: "Non merci",
    remembered: "C'est noté. La prochaine fois, je te demanderai juste si ça tient toujours.",
    notRemembered: "D'accord, je ne retiens rien.", remembersLabel: "Iris se souvient de tes goûts",
    forgotten: "Oublié. Je te redemanderai la prochaine fois.",
  },
  es: {
    continue: "Seguir", skip: "Saltar", directions: "Muéstrame las direcciones", forget: "Olvida mis gustos",
    rememberAsk: "¿Recuerdo lo que te gusta para entender mejor tu próxima película?",
    rememberYes: "Sí, por favor", rememberNo: "No, gracias",
    remembered: "Anotado. La próxima vez solo te preguntaré si sigue igual.",
    notRemembered: "Vale, no recuerdo nada.", remembersLabel: "Iris recuerda tus gustos",
    forgotten: "Olvidado. La próxima vez te lo vuelvo a preguntar.",
  },
  tr: {
    continue: "Devam", skip: "Geç", directions: "Yönleri göster", forget: "Zevkimi unut",
    rememberAsk: "Neleri sevdiğini hatırlayayım mı, bir sonraki filmini hemen daha iyi anlamak için?",
    rememberYes: "Evet, lütfen", rememberNo: "Hayır, teşekkürler",
    remembered: "Tamam, hatırlıyorum. Bir dahaki sefere sadece hâlâ geçerli mi diye soracağım.",
    notRemembered: "Tamam, hiçbir şey hatırlamıyorum.", remembersLabel: "Iris zevkini hatırlıyor",
    forgotten: "Unuttum. Bir dahaki sefere yine sorarım.",
  },
};

export function icebreakerCopy(): IcebreakerCopy {
  return ICEBREAKER[makerLanguage()];
}

/** The question at the end of the intake: does Iris lead, or help when asked? */
export interface ModeCopy {
  ask: string;
  guided: string;
  independent: string;
  guidedSaid: string;
  independentSaid: string;
}

const MODE: Record<IrisLanguage, ModeCopy> = {
  en: {
    ask: "How shall we go on: shall I take you along step by step, or do you prefer to direct yourself with me as your help?",
    guided: "Iris takes me along", independent: "I direct myself",
    guidedSaid: "Lovely, I'll take you along. You can switch any time in my panel.",
    independentSaid: "Fine, you direct. Tap me whenever you want help.",
  },
  nl: {
    ask: "Hoe wil je straks verder: zal ik je stap voor stap meenemen, of regisseer je liever zelf met mij als hulp?",
    guided: "Iris neemt me mee", independent: "Ik regisseer zelf",
    guidedSaid: "Fijn, ik neem je mee. Je kunt altijd wisselen in mijn paneel.",
    independentSaid: "Prima, jij regisseert. Klik op mij als je hulp wilt.",
  },
  de: {
    ask: "Wie machen wir weiter: Soll ich dich Schritt für Schritt mitnehmen, oder führst du lieber selbst Regie, mit mir als Hilfe?",
    guided: "Iris nimmt mich mit", independent: "Ich führe selbst Regie",
    guidedSaid: "Schön, ich nehme dich mit. Du kannst jederzeit in meinem Panel wechseln.",
    independentSaid: "Gut, du führst Regie. Tipp mich an, wenn du Hilfe willst.",
  },
  fr: {
    ask: "Comment continuer : je t'accompagne pas à pas, ou tu préfères réaliser toi-même, avec moi en soutien ?",
    guided: "Iris m'accompagne", independent: "Je réalise moi-même",
    guidedSaid: "Parfait, je t'accompagne. Tu peux changer à tout moment dans mon panneau.",
    independentSaid: "D'accord, tu réalises. Touche-moi quand tu veux de l'aide.",
  },
  es: {
    ask: "¿Cómo seguimos: te acompaño paso a paso o prefieres dirigir tú, conmigo como ayuda?",
    guided: "Iris me acompaña", independent: "Dirijo yo",
    guidedSaid: "Genial, te acompaño. Puedes cambiar cuando quieras en mi panel.",
    independentSaid: "Bien, diriges tú. Tócame cuando quieras ayuda.",
  },
  tr: {
    ask: "Nasıl devam edelim: seni adım adım götüreyim mi, yoksa yardımcın olarak ben yanındayken kendin mi yönetmek istersin?",
    guided: "Iris bana yol göstersin", independent: "Kendim yöneteceğim",
    guidedSaid: "Güzel, sana yol gösteriyorum. Panelimden istediğin zaman değiştirebilirsin.",
    independentSaid: "Tamam, sen yönetiyorsun. Yardım istediğinde bana dokun.",
  },
};

export function modeCopy(): ModeCopy {
  return MODE[makerLanguage()];
}

/** What is being made (#cliptype): the welcome's three choices and what the maker says by choosing one. */
export interface TypeCopy {
  ask: string;
  lyric: string;
  cinematic: string;
  shorts: string;
  said: { lyric: string; cinematic: string; shorts: string };
}

const TYPE: Record<IrisLanguage, TypeCopy> = {
  en: {
    ask: "What are we making?",
    lyric: "Lyric video · images and words", cinematic: "Cinematic clip · moving shots", shorts: "Shorts for socials",
    said: { lyric: "I'm making a lyric video: images with the words on screen.", cinematic: "I'm making a cinematic clip with moving shots.",
      shorts: "I mainly want shorts for socials: a lyric video first, then vertical shorts from it." },
  },
  nl: {
    ask: "Wat gaan we maken?",
    lyric: "Lyric video · beelden en tekst", cinematic: "Cinematische clip · bewegende shots", shorts: "Shorts voor socials",
    said: { lyric: "Ik maak een lyric video: beelden met de tekst in beeld.", cinematic: "Ik maak een cinematische clip met bewegende shots.",
      shorts: "Ik wil vooral shorts voor socials: eerst een lyric video, daarna verticale shorts eruit." },
  },
  de: {
    ask: "Was machen wir?",
    lyric: "Lyric-Video · Bilder und Text", cinematic: "Kinoclip · bewegte Shots", shorts: "Shorts für Social Media",
    said: { lyric: "Ich mache ein Lyric-Video: Bilder mit dem Text im Bild.", cinematic: "Ich mache einen Kinoclip mit bewegten Shots.",
      shorts: "Ich will vor allem Shorts für Social Media: erst ein Lyric-Video, dann vertikale Shorts daraus." },
  },
  fr: {
    ask: "Qu'est-ce qu'on fait ?",
    lyric: "Lyric video · images et paroles", cinematic: "Clip cinématique · plans animés", shorts: "Shorts pour les réseaux",
    said: { lyric: "Je fais une lyric video : des images avec les paroles à l'écran.", cinematic: "Je fais un clip cinématique avec des plans animés.",
      shorts: "Je veux surtout des shorts pour les réseaux : d'abord une lyric video, puis des shorts verticaux." },
  },
  es: {
    ask: "¿Qué hacemos?",
    lyric: "Lyric video · imágenes y letra", cinematic: "Clip cinematográfico · planos en movimiento", shorts: "Shorts para redes",
    said: { lyric: "Hago un lyric video: imágenes con la letra en pantalla.", cinematic: "Hago un clip cinematográfico con planos en movimiento.",
      shorts: "Quiero sobre todo shorts para redes: primero un lyric video y luego shorts verticales." },
  },
  tr: {
    ask: "Ne yapıyoruz?",
    lyric: "Lyric video · görseller ve sözler", cinematic: "Sinematik klip · hareketli çekimler", shorts: "Sosyal medya için shorts",
    said: { lyric: "Bir lyric video yapıyorum: sözlerin ekranda olduğu görseller.", cinematic: "Hareketli çekimlerle sinematik bir klip yapıyorum.",
      shorts: "Özellikle sosyal medya için shorts istiyorum: önce bir lyric video, sonra ondan dikey shorts." },
  },
};

export function typeCopy(): TypeCopy {
  return TYPE[makerLanguage()];
}

/** The intake as subtitles: the few words of the screen around Iris's lines. */
export interface SubtitleCopy {
  you: string;
  readBack: string;
  close: string;
  listening: string;
  retry: string;
  ideasOnBoard: string;
  castAgain: string;
  castOffer: string;
  pasted: (lines: number) => string;
}

const SUBTITLE: Record<IrisLanguage, SubtitleCopy> = {
  en: { you: "you", readBack: "Read back", close: "Back to Iris", listening: "I'm listening…", retry: "Try again",
    ideasOnBoard: "I've put three directions on your board.",
    castAgain: "Choose your cast", castOffer: "Who plays in your film? Choose here; everything else stays.", pasted: (lines) => `Lyrics pasted · ${lines} lines` },
  nl: { you: "jij", readBack: "Lees terug", close: "Terug naar Iris", listening: "Ik luister…", retry: "Probeer opnieuw",
    ideasOnBoard: "Ik heb drie richtingen op je bord gezet.",
    castAgain: "Kies je cast", castOffer: "Wie speelt er in je film? Kies hier; de rest blijft.", pasted: (lines) => `Songtekst geplakt · ${lines} regels` },
  de: { you: "du", readBack: "Nachlesen", close: "Zurück zu Iris", listening: "Ich höre zu…", retry: "Noch einmal",
    ideasOnBoard: "Ich habe drei Richtungen auf dein Board gelegt.",
    castAgain: "Wähle deine Besetzung", castOffer: "Wer spielt in deinem Film? Wähle hier; alles andere bleibt.", pasted: (lines) => `Songtext eingefügt · ${lines} Zeilen` },
  fr: { you: "toi", readBack: "Relire", close: "Retour à Iris", listening: "J'écoute…", retry: "Réessayer",
    ideasOnBoard: "J'ai mis trois directions sur ton tableau.",
    castAgain: "Choisis ton casting", castOffer: "Qui joue dans ton film ? Choisis ici ; tout le reste reste.", pasted: (lines) => `Paroles collées · ${lines} lignes` },
  es: { you: "tú", readBack: "Releer", close: "Volver con Iris", listening: "Te escucho…", retry: "Reintentar",
    ideasOnBoard: "He puesto tres direcciones en tu tablero.",
    castAgain: "Elige tu reparto", castOffer: "¿Quién actúa en tu película? Elige aquí; todo lo demás se queda.", pasted: (lines) => `Letra pegada · ${lines} líneas` },
  tr: { you: "sen", readBack: "Geri oku", close: "Iris'e dön", listening: "Dinliyorum…", retry: "Tekrar dene",
    ideasOnBoard: "Panona üç yön koydum.",
    castAgain: "Oyuncularını seç", castOffer: "Filminde kim oynuyor? Buradan seç; geri kalan her şey aynı kalır.", pasted: (lines) => `Şarkı sözü yapıştırıldı · ${lines} satır` },
};

export function subtitleCopy(): SubtitleCopy {
  return SUBTITLE[makerLanguage()];
}

/** The production setup on the board: how much of a cinematic clip moves. */
export type SetupKey = "best" | "good" | "economy";
export interface SetupCopy {
  title: string;
  names: Record<SetupKey, string>;
  lines: Record<SetupKey, string>;
  recommended: string;
  /** Under an option whose price is above the server's safety limit. */
  overLimit: string;
  note: (name: string) => string;
}
const setup = (title: string, names: [string, string, string], lines: [string, string, string], recommended: string, overLimit: string): SetupCopy => ({
  title,
  names: { best: names[0], good: names[1], economy: names[2] },
  lines: { best: lines[0], good: lines[1], economy: lines[2] },
  recommended,
  overLimit,
  note: (name) => `✎ ${title}: ${name}`,
});
const SETUP: Record<IrisLanguage, SetupCopy> = {
  en: setup("Setup", ["Best", "Good", "Economy"],
    ["Every shot moves.", "Moves where it counts; the rest are stills with depth.", "Stills with depth and camera moves; no moving lips."], "Iris recommends",
    "Above this server's limit: choose fewer shots or another setup."),
  nl: setup("Opzet", ["Beste", "Goed", "Economisch"],
    ["Elke shot beweegt.", "Beweegt waar het telt; de rest zijn stills met diepte.", "Stills met diepte en camerabeweging; geen bewegende lippen."], "Iris raadt aan",
    "Boven de limiet van deze server: kies minder shots of een andere opzet."),
  de: setup("Aufbau", ["Beste", "Gut", "Sparsam"],
    ["Jede Einstellung bewegt sich.", "Bewegt sich, wo es zählt; der Rest sind Standbilder mit Tiefe.", "Standbilder mit Tiefe und Kamerafahrt; keine bewegten Lippen."], "Iris empfiehlt",
    "Über dem Limit dieses Servers: wähle weniger Einstellungen oder einen anderen Aufbau."),
  fr: setup("Mise en place", ["Meilleure", "Bonne", "Économique"],
    ["Chaque plan bouge.", "Bouge là où ça compte ; le reste en images fixes avec profondeur.", "Images fixes avec profondeur et mouvement de caméra ; pas de lèvres qui bougent."], "Iris recommande",
    "Au-dessus de la limite de ce serveur : choisis moins de plans ou une autre mise en place."),
  es: setup("Planteamiento", ["Mejor", "Buena", "Económica"],
    ["Cada plano se mueve.", "Se mueve donde importa; el resto son imágenes fijas con profundidad.", "Imágenes fijas con profundidad y movimiento de cámara; sin labios en movimiento."], "Iris recomienda",
    "Por encima del límite de este servidor: elige menos planos u otro planteamiento."),
  tr: setup("Kurulum", ["En iyi", "İyi", "Ekonomik"],
    ["Her çekim hareket eder.", "Önemli yerlerde hareket eder; gerisi derinlikli durağan görüntüler.", "Derinlikli durağan görüntüler ve kamera hareketi; hareketli dudak yok."], "Iris öneriyor",
    "Bu sunucunun sınırının üstünde: daha az çekim ya da başka bir kurulum seç."),
};

export function setupCopy(): SetupCopy {
  return SETUP[makerLanguage()];
}

/** Iris in the top bar: her check of the film, in soft words — "things to look at", never "issues". */
export interface ScanCopy {
  checked: string;
  things: (n: number) => string;
  allGood: string;
  fix: string;
  of: (index: number, total: number) => string;
  skip: string;
  ask: string;
  nextStep: string;
  kinds: Record<"cast" | "place" | "plan" | "story" | "decision" | "review" | "picture" | "job" | "repaired", string>;
}

const SCAN: Record<IrisLanguage, ScanCopy> = {
  en: {
    checked: "Iris checked your film",
    things: (n) => `${n} ${n === 1 ? "thing" : "things"} to look at`,
    allGood: "all good ✓",
    fix: "Fix with Iris",
    of: (index, total) => `${index} of ${total}`,
    skip: "Skip for now",
    ask: "Ask Iris anything…",
    nextStep: "Next step",
    kinds: { cast: "cast", place: "place", plan: "shot plan", story: "story check", decision: "decision", review: "review", picture: "pictures", job: "stopped", repaired: "repaired" },
  },
  nl: {
    checked: "Iris heeft je film nagekeken",
    things: (n) => `${n} ${n === 1 ? "ding" : "dingen"} om naar te kijken`,
    allGood: "alles klopt ✓",
    fix: "Samen met Iris",
    of: (index, total) => `${index} van ${total}`,
    skip: "Later",
    ask: "Vraag Iris iets…",
    nextStep: "Volgende stap",
    kinds: { cast: "cast", place: "plek", plan: "shotplan", story: "verhaal", decision: "keuze", review: "nakijken", picture: "beelden", job: "gestopt", repaired: "gerepareerd" },
  },
  de: {
    checked: "Iris hat deinen Film geprüft",
    things: (n) => `${n} ${n === 1 ? "Sache" : "Sachen"} zum Anschauen`,
    allGood: "alles passt ✓",
    fix: "Mit Iris lösen",
    of: (index, total) => `${index} von ${total}`,
    skip: "Später",
    ask: "Frag Iris etwas…",
    nextStep: "Nächster Schritt",
    kinds: { cast: "Besetzung", place: "Ort", plan: "Shotplan", story: "Story", decision: "Entscheidung", review: "Prüfen", picture: "Bilder", job: "gestoppt", repaired: "repariert" },
  },
  fr: {
    checked: "Iris a vérifié ton film",
    things: (n) => `${n} ${n === 1 ? "point" : "points"} à regarder`,
    allGood: "tout est bon ✓",
    fix: "Régler avec Iris",
    of: (index, total) => `${index} sur ${total}`,
    skip: "Plus tard",
    ask: "Demande à Iris…",
    nextStep: "Étape suivante",
    kinds: { cast: "casting", place: "lieu", plan: "découpage", story: "histoire", decision: "choix", review: "à vérifier", picture: "images", job: "arrêté", repaired: "réparé" },
  },
  es: {
    checked: "Iris ha revisado tu película",
    things: (n) => `${n} ${n === 1 ? "cosa" : "cosas"} que mirar`,
    allGood: "todo bien ✓",
    fix: "Resolver con Iris",
    of: (index, total) => `${index} de ${total}`,
    skip: "Más tarde",
    ask: "Pregunta a Iris…",
    nextStep: "Siguiente paso",
    kinds: { cast: "reparto", place: "lugar", plan: "plan de planos", story: "historia", decision: "decisión", review: "revisar", picture: "imágenes", job: "detenido", repaired: "reparado" },
  },
  tr: {
    checked: "Iris filmini kontrol etti",
    things: (n) => `bakılacak ${n} şey`,
    allGood: "her şey yolunda ✓",
    fix: "Iris ile çöz",
    of: (index, total) => `${index} / ${total}`,
    skip: "Sonra",
    ask: "Iris'e bir şey sor…",
    nextStep: "Sonraki adım",
    kinds: { cast: "oyuncular", place: "mekân", plan: "çekim planı", story: "hikâye", decision: "karar", review: "kontrol", picture: "görseller", job: "durdu", repaired: "onarıldı" },
  },
};

export function scanCopy(): ScanCopy {
  return SCAN[makerLanguage()];
}

/** Sing with your photo: the chip, the upload and the consent, in the maker's language. */
export interface PhotoCopy {
  chip: string;
  tip: string;
  label: string;
  consent: string;
  continue: string;
  change: string;
  ready: string;
  wrongType: string;
  tooLarge: string;
  sourceMine: string;
  sourceCast: string;
  sourceNew: string;
  describe: string;
  saveToCast: string;
}

const PHOTO: Record<IrisLanguage, PhotoCopy> = {
  en: {
    chip: "🎤 Let me sing it myself with my photo",
    tip: "A clear photo of your face and shoulders, looking into the camera, in good light.",
    label: "Your photo",
    consent: "This is me, or I have permission from the person in the photo, and they are 18 or older.",
    continue: "Continue",
    change: "Choose another photo",
    ready: "My photo is ready",
    wrongType: "Use a JPEG, PNG or WebP photo.",
    tooLarge: "The photo is larger than 15 MB.",
    sourceMine: "My photo",
    sourceCast: "Someone from my cast",
    sourceNew: "Design a new singer",
    describe: "Describe your singer in a sentence: age, hair, look, mood.",
    saveToCast: "Save to my cast",
  },
  nl: {
    chip: "🎤 Laat mij zelf zingen met mijn foto",
    tip: "Een duidelijke foto van je gezicht en schouders, recht in de camera, goed licht.",
    label: "Jouw foto",
    consent: "Dit ben ik, of ik heb toestemming van de persoon op de foto, en die is 18 jaar of ouder.",
    continue: "Verder",
    change: "Kies een andere foto",
    ready: "Mijn foto staat klaar",
    wrongType: "Gebruik een JPEG-, PNG- of WebP-foto.",
    tooLarge: "De foto is groter dan 15 MB.",
    sourceMine: "Mijn foto",
    sourceCast: "Iemand uit mijn cast",
    sourceNew: "Ontwerp een nieuwe zanger",
    describe: "Beschrijf je zanger in één zin: leeftijd, haar, uitstraling, sfeer.",
    saveToCast: "Bewaar in mijn cast",
  },
  de: {
    chip: "🎤 Lass mich selbst mit meinem Foto singen",
    tip: "Ein klares Foto von Gesicht und Schultern, Blick in die Kamera, gutes Licht.",
    label: "Dein Foto",
    consent: "Das bin ich, oder ich habe die Erlaubnis der Person auf dem Foto, und sie ist 18 Jahre oder älter.",
    continue: "Weiter",
    change: "Anderes Foto wählen",
    ready: "Mein Foto ist bereit",
    wrongType: "Nutze ein JPEG-, PNG- oder WebP-Foto.",
    tooLarge: "Das Foto ist größer als 15 MB.",
    sourceMine: "Mein Foto",
    sourceCast: "Jemand aus meiner Besetzung",
    sourceNew: "Neue Sängerin oder neuen Sänger entwerfen",
    describe: "Beschreibe deine Stimme in einem Satz: Alter, Haare, Ausstrahlung, Stimmung.",
    saveToCast: "In meiner Besetzung speichern",
  },
  fr: {
    chip: "🎤 Laisse-moi chanter moi-même avec ma photo",
    tip: "Une photo nette du visage et des épaules, regard vers l'objectif, bonne lumière.",
    label: "Ta photo",
    consent: "C'est moi, ou j'ai l'autorisation de la personne sur la photo, et elle a 18 ans ou plus.",
    continue: "Continuer",
    change: "Choisir une autre photo",
    ready: "Ma photo est prête",
    wrongType: "Utilise une photo JPEG, PNG ou WebP.",
    tooLarge: "La photo dépasse 15 Mo.",
    sourceMine: "Ma photo",
    sourceCast: "Quelqu'un de mon casting",
    sourceNew: "Créer un nouveau chanteur",
    describe: "Décris ton chanteur en une phrase : âge, cheveux, allure, ambiance.",
    saveToCast: "Garder dans mon casting",
  },
  es: {
    chip: "🎤 Déjame cantarla yo con mi foto",
    tip: "Una foto clara de tu cara y hombros, mirando a la cámara, con buena luz.",
    label: "Tu foto",
    consent: "Soy yo, o tengo permiso de la persona de la foto, y tiene 18 años o más.",
    continue: "Continuar",
    change: "Elegir otra foto",
    ready: "Mi foto está lista",
    wrongType: "Usa una foto JPEG, PNG o WebP.",
    tooLarge: "La foto supera los 15 MB.",
    sourceMine: "Mi foto",
    sourceCast: "Alguien de mi reparto",
    sourceNew: "Diseñar un nuevo cantante",
    describe: "Describe a tu cantante en una frase: edad, pelo, estilo, ambiente.",
    saveToCast: "Guardar en mi reparto",
  },
  tr: {
    chip: "🎤 Fotoğrafımla kendim söyleyeyim",
    tip: "Yüzünün ve omuzlarının net bir fotoğrafı, kameraya bakarken, iyi ışıkta.",
    label: "Fotoğrafın",
    consent: "Bu benim ya da fotoğraftaki kişiden iznim var ve kişi 18 yaşında veya daha büyük.",
    continue: "Devam",
    change: "Başka bir fotoğraf seç",
    ready: "Fotoğrafım hazır",
    wrongType: "JPEG, PNG veya WebP fotoğraf kullan.",
    tooLarge: "Fotoğraf 15 MB'tan büyük.",
    sourceMine: "Fotoğrafım",
    sourceCast: "Oyuncularımdan biri",
    sourceNew: "Yeni bir şarkıcı tasarla",
    describe: "Şarkıcını bir cümleyle anlat: yaş, saç, tarz, hava.",
    saveToCast: "Oyuncularıma kaydet",
  },
};

export function photoCopy(): PhotoCopy {
  return PHOTO[makerLanguage()];
}
